import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";

interface JobDescriptionExtensionProps {
  jobId: string | number;
  currentDescription?: string;
  onAccept?: (description: string) => void;
}

interface EnhancedData {
  enhancedDescription?: string;
  description?: string;
  summary?: string;
  responsibilities?: string[];
  requiredQualifications?: string[];
  preferredQualifications?: string[];
  companyCultureFit?: string;
  benefitsAndPerks?: string[];
}

interface ApiResult {
  success?: boolean;
  enhanced?: EnhancedData;
  data?: {
    enhanced?: EnhancedData;
  };
}

const JobDescriptionExtension: React.FC<JobDescriptionExtensionProps> = ({
  jobId,
  currentDescription,
  onAccept,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [extendedDescription, setExtendedDescription] = useState<string | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug: Track state changes
  useEffect(() => {
    console.log(
      "State updated - extendedDescription:",
      extendedDescription ? `Length: ${extendedDescription.length}` : "null",
    );
    console.log("State updated - isGenerating:", isGenerating);
    console.log("State updated - error:", error);
  }, [extendedDescription, isGenerating, error]);

  const extendMutation = useMutation<ApiResult, Error>(
    async () => {
      const response = (await recruitingAPI.extendJobDescription(jobId, {})) as
        | { data?: ApiResult }
        | ApiResult;
      console.log("API Response (full):", response);
      // The API service returns response.data from axios, which is: { success: true, data: {...}, message: '...' }
      // So we need to access response.data to get the actual result object
      const result = (response as { data?: ApiResult })?.data || response;
      console.log("Extracted result:", result);
      console.log("Result keys:", result ? Object.keys(result) : "null");
      return result as ApiResult;
    },
    {
      onSuccess: (result) => {
        console.log("Mutation onSuccess - result:", result);
        console.log("Result type:", typeof result);
        console.log("Result keys:", result ? Object.keys(result) : "null");

        setIsGenerating(false);
        setError(null);

        // Handle different response structures
        let enhancedText = "";

        // The API response structure after backend processing:
        // result = { success: true, originalDescription: "...", enhanced: { enhancedDescription: "full text with sections", ... }, generatedAt: "..." }
        // OR nested: result.data = { success: true, originalDescription: "...", enhanced: { enhancedDescription: "...", ... } }

        let enhancedData: EnhancedData | null = null;

        // Check for nested structure first (result.data.enhanced)
        if (result?.data?.enhanced) {
          enhancedData = result.data.enhanced;
          console.log("Found nested enhanced data (result.data.enhanced)");
        } else if (result?.enhanced) {
          enhancedData = result.enhanced;
          console.log("Found direct enhanced data (result.enhanced)");
        }

        if (enhancedData) {
          console.log("Enhanced data keys:", Object.keys(enhancedData));

          // The backend now returns enhancedDescription with all sections formatted
          if (enhancedData.enhancedDescription?.trim()) {
            enhancedText = enhancedData.enhancedDescription;
            console.log(
              "Using enhancedDescription (comprehensive), length:",
              enhancedText.length,
            );
          } else if (enhancedData.description?.trim()) {
            enhancedText = enhancedData.description;
            console.log("Using description field");
          } else if (typeof enhancedData === "string") {
            enhancedText = enhancedData;
            console.log("Enhanced is a string");
          } else {
            // Fallback: construct from structured data if enhancedDescription is missing
            console.log("Constructing from structured data (fallback)");
            const parts: string[] = [];
            if (enhancedData.summary) parts.push(enhancedData.summary);
            if (
              enhancedData.responsibilities &&
              Array.isArray(enhancedData.responsibilities)
            ) {
              parts.push("\n\n## Key Responsibilities\n");
              enhancedData.responsibilities.forEach((r, i) => {
                parts.push(`${i + 1}. ${r}`);
              });
            }
            if (
              enhancedData.requiredQualifications &&
              Array.isArray(enhancedData.requiredQualifications)
            ) {
              parts.push("\n\n## Required Qualifications\n");
              enhancedData.requiredQualifications.forEach((q, i) => {
                parts.push(`${i + 1}. ${q}`);
              });
            }
            if (
              enhancedData.preferredQualifications &&
              Array.isArray(enhancedData.preferredQualifications)
            ) {
              parts.push("\n\n## Preferred Qualifications\n");
              enhancedData.preferredQualifications.forEach((q, i) => {
                parts.push(`${i + 1}. ${q}`);
              });
            }
            if (enhancedData.companyCultureFit) {
              parts.push(
                "\n\n## Company Culture Fit\n",
                enhancedData.companyCultureFit,
              );
            }
            if (
              enhancedData.benefitsAndPerks &&
              Array.isArray(enhancedData.benefitsAndPerks)
            ) {
              parts.push("\n\n## Benefits and Perks\n");
              enhancedData.benefitsAndPerks.forEach((b, i) => {
                parts.push(`${i + 1}. ${b}`);
              });
            }
            enhancedText = parts.join("\n");
            console.log("Constructed text length:", enhancedText.length);
          }
        } else if (result && typeof result === "string") {
          enhancedText = result;
          console.log("Result is a string");
        } else {
          console.warn("Unexpected result structure:", result);
          console.warn("Full result JSON:", JSON.stringify(result, null, 2));
        }

        if (enhancedText?.trim()) {
          console.log(
            "Setting extended description, length:",
            enhancedText.length,
          );
          console.log("First 200 chars:", enhancedText.substring(0, 200));
          // Use functional update to ensure state is set
          setExtendedDescription(() => enhancedText);
          console.log("State setter called");
        } else {
          const errorMsg =
            "Received response but could not extract description. Please check the console for details.";
          setError(errorMsg);
          console.error(
            "Failed to extract description. Full result:",
            JSON.stringify(result, null, 2),
          );
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        setIsGenerating(false);
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to extend description";
        setError(errorMessage);
        console.error("Error extending job description:", err);
      },
    },
  );

  const handleExtend = () => {
    console.log("handleExtend called");
    setIsGenerating(true);
    setError(null);
    setExtendedDescription(null); // Clear previous result
    extendMutation.mutate();
  };

  // Sync isGenerating with mutation state
  useEffect(() => {
    if (extendMutation.isLoading !== isGenerating) {
      setIsGenerating(extendMutation.isLoading);
    }
  }, [extendMutation.isLoading, isGenerating]);

  const handleAccept = () => {
    if (extendedDescription && onAccept) {
      onAccept(extendedDescription);
    }
  };

  const handleEdit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setExtendedDescription(e.target.value);
  };

  return (
    <div className="job-description-extension">
      <div className="extension-header">
        <h3>{t("recruiting:jobs.form.buttons.aiExtend")}</h3>
        <button
          className="btn-primary"
          onClick={handleExtend}
          disabled={isGenerating}
        >
          {isGenerating
            ? t("recruiting:jobs.form.buttons.extending")
            : t("recruiting:jobs.form.buttons.aiExtend")}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {extendedDescription && (
        <div className="extension-content">
          <div className="description-comparison">
            <div className="description-section">
              <h4>{t("recruiting:jobs.form.fields.description")}</h4>
              <div className="description-text">
                {currentDescription || "No description provided"}
              </div>
            </div>

            <div className="description-section">
              <h4>{t("recruiting:jobs.form.buttons.aiExtend")}</h4>
              <textarea
                className="description-textarea"
                value={extendedDescription}
                onChange={handleEdit}
                rows={15}
              />
            </div>
          </div>

          <div className="extension-actions">
            <button className="btn-primary" onClick={handleAccept}>
              {t("common:actions.confirm")}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setExtendedDescription(null)}
            >
              {t("common:actions.cancel")}
            </button>
          </div>
        </div>
      )}

      {!extendedDescription && !isGenerating && (
        <div className="extension-info">
          <p>
            Click &quot;Extend Description&quot; to use AI to enhance and expand
            the job description with detailed responsibilities, qualifications,
            and benefits.
          </p>
        </div>
      )}
    </div>
  );
};

export default JobDescriptionExtension;
